/**
 * Coordinates a complete Flow composition for one dashboard instance.
 * It turns loader emissions and chart interactions into view models for the flow chart, net chart,
 * KPI tiles and breakdown tables while keeping the individual projections stateless and reusable.
 * Required to combine these modules and own the shared zoom, threshold and series-visibility state.
 */
import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import type { ECharts } from 'echarts';
import { Subscription } from 'rxjs';
import type {
    FlowChartViewModel,
    NetChartViewModel,
} from 'src/app/dashboards/common/items/flow-chart.models';
import { ChartColorService } from 'src/app/services/chart-color.service';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { IDataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import {
    FlowBreakdownRow,
    FlowBreakdownSort,
    FlowRangeAggregation,
    isFlowDashboardData,
} from 'src/app/services/flow-dashboard/flow-dashboard.models';
import { IQueryContext } from 'src/app/services/query-context/models/IQueryContext';
import { CategoryLabelSortFn } from 'src/app/utils/echarts/xy-chart-axis';

import {
    aggregateFlowRows,
    FlowAggregation,
    getFlowVisibleDates,
} from './flow-dashboard-aggregation';
import { buildFlowBreakdown } from './flow-dashboard-breakdown';
import { FlowDashboardChartInteractions } from './flow-dashboard-chart-interactions';
import {
    buildFlowPresentation,
    flowNetByDate,
    FlowPresentation,
} from './flow-dashboard-presentation';

/** Config any dashboard passes to {@link FlowDashboardStateService.attach}. */
export interface FlowDashboardConfig {
    /** Base instance id; the `echarts.connect` group derives from it. */
    instanceId: string;
    /** Row keys emitted by the loader. Defaults: date / inflow / outflow / split_group. */
    dateKey?: string;
    inflowKey?: string;
    outflowKey?: string;
    splitKey?: string;
    /** Show the net-balance companion chart (connected). Default true. */
    showNetChart?: boolean;
    /** "Remaining" bucket threshold options + default (client-side, no re-query). */
    thresholdOptions?: { value: number; label: string }[];
    defaultThreshold?: number;
    /** Row key carrying the bucket/group value emitted by the loader (present when a filter is bucketed). Default 'bucket_group'. */
    bucketKey?: string;
    /** Category-axis comparator. Omitted = plain string order. */
    xAxisSortFn?: CategoryLabelSortFn;
    /** Flow identity uses inflow/outflow shades; standard uses the shared categorical palette. */
    colorMode?: 'flow' | 'standard';
    /** Categorical-palette namespace for standard colour mode. Default 'flow-dashboard'. */
    colorLayer?: string;
}

export const DEFAULT_FLOW_THRESHOLD_OPTIONS = [
    { value: 0.005, label: '0.5%' },
    { value: 0.01, label: '1%' },
    { value: 0.02, label: '2%' },
    { value: 0.05, label: '5%' },
    { value: 0.1, label: '10%' },
];

/**
 * Per-dashboard coordinator for a two-sided Flow dashboard. It owns data arrival and interaction state;
 * presentation, breakdown construction and ECharts wiring live in focused modules.
 */
@Injectable()
export class FlowDashboardStateService implements OnDestroy {
    private static readonly BREAKDOWN_SORT_CYCLE: readonly FlowBreakdownSort[] =
        ['ranking', 'desc', 'asc'];
    private static readonly BREAKDOWN_SORT_LABELS: Readonly<
        Record<FlowBreakdownSort, string>
    > = {
        ranking: 'Ranking',
        desc: 'Value ↓',
        asc: 'Value ↑',
    };

    readonly flowViewModel$ = new EventEmitter<IDataLoaderResult>();
    readonly netViewModel$ = new EventEmitter<IDataLoaderResult>();
    readonly inflowRows$ = new EventEmitter<IDataLoaderResult>();
    readonly outflowRows$ = new EventEmitter<IDataLoaderResult>();

    thresholdOptions = DEFAULT_FLOW_THRESHOLD_OPTIONS;
    threshold = 0.01;
    showNetChart = true;
    /** Chart-view toggle: zoom the flow/net charts with the mouse wheel. Default on. */
    wheelZoom = true;
    /** Per-side client-only row order; defaults to chart stacking. */
    breakdownSort: Record<'in' | 'out', FlowBreakdownSort> = {
        in: 'ranking',
        out: 'ranking',
    };

    private dateKey = 'date';
    private inflowKey = 'inflow';
    private outflowKey = 'outflow';
    private splitKey = 'split_group';
    private bucketKey = 'bucket_group';
    private xAxisSortFn?: CategoryLabelSortFn;
    private colorMode: 'flow' | 'standard' = 'standard';
    private colorLayer = 'flow-dashboard';
    private lastContext?: IQueryContext;
    private aggregation: FlowAggregation = {
        dates: [],
        groups: {},
        byBucket: {},
        bucketValues: [],
        groupByBucket: false,
    };
    private presentation?: FlowPresentation;
    private hiddenSeries = new Set<string>();
    private isApplyingLegendSelection = false;
    private zoomStart = 0;
    private zoomEnd = 100;
    private rangeAggregation: FlowRangeAggregation = 'sum';
    private sub?: Subscription;
    private readonly chartInteractions: FlowDashboardChartInteractions;

    constructor(
        private dashboard: DashboardService,
        private colorService: ChartColorService
    ) {
        this.chartInteractions = new FlowDashboardChartInteractions(dashboard, {
            onDataZoom: (params) => this.onDataZoom(params),
            onLegendSelectChanged: (event) => this.onLegendSelectChanged(event),
            onFullscreenExit: () => {
                if (this.aggregation.dates.length) {
                    this.emitNet();
                }
            },
        });
    }

    /** Wire the dashboard's fetch-once loader + config. Call once from the dashboard's ngOnInit. */
    attach(
        emitter: EventEmitter<IDataLoaderResult>,
        config: FlowDashboardConfig
    ) {
        this.dateKey = config.dateKey ?? 'date';
        this.inflowKey = config.inflowKey ?? 'inflow';
        this.outflowKey = config.outflowKey ?? 'outflow';
        this.splitKey = config.splitKey ?? 'split_group';
        this.bucketKey = config.bucketKey ?? 'bucket_group';
        this.xAxisSortFn = config.xAxisSortFn;
        this.colorMode = config.colorMode ?? 'standard';
        this.colorLayer = config.colorLayer ?? 'flow-dashboard';
        this.thresholdOptions =
            config.thresholdOptions ?? DEFAULT_FLOW_THRESHOLD_OPTIONS;
        this.threshold = config.defaultThreshold ?? this.threshold;
        this.showNetChart = config.showNetChart ?? true;
        this.sub?.unsubscribe();
        this.sub = emitter.subscribe((result) => this.onLoaderResult(result));
        this.chartInteractions.configure(config.instanceId, this.showNetChart);
    }

    ngOnDestroy() {
        this.sub?.unsubscribe();
        this.chartInteractions.destroy();
    }

    /** Register a chart instance (idempotent) and sync it with the companion chart when applicable. */
    ensureWired(instance: ECharts, role: 'flow' | 'net') {
        this.chartInteractions.wire(instance, role);
    }

    /** Threshold changed client-side: reset transient view state and rebuild derived output. */
    setThreshold(value: number) {
        this.threshold = value;
        this.hiddenSeries.clear();
        this.zoomStart = 0;
        this.zoomEnd = 100;
        this.emitFlow();
        this.refreshVisibleAggregates();
    }

    /** Cycle one table's client-side row order. */
    cycleBreakdownSort(dir: 'in' | 'out') {
        const cycle = FlowDashboardStateService.BREAKDOWN_SORT_CYCLE;
        const next =
            cycle[(cycle.indexOf(this.breakdownSort[dir]) + 1) % cycle.length];
        this.breakdownSort[dir] = next;
        this.emitBreakdown();
    }

    /** Current row-order label for a table header. */
    breakdownSortLabel(dir: 'in' | 'out') {
        return FlowDashboardStateService.BREAKDOWN_SORT_LABELS[
            this.breakdownSort[dir]
        ];
    }

    /** Wheel-zoom toggled client-side: re-emit so both charts adopt the new inside-dataZoom mode. */
    setWheelZoom(enabled: boolean) {
        this.wheelZoom = enabled;
        if (this.aggregation.dates.length) {
            this.emitFlow();
        }
    }

    /** Toggle a flow series from a breakdown-table row, preserving the chart's native legend behaviour. */
    toggleSeries(name: string) {
        if (this.hiddenSeries.has(name)) {
            this.hiddenSeries.delete(name);
        } else {
            this.hiddenSeries.add(name);
        }
        this.dashboardFlowChartAction({ type: 'legendToggleSelect', name });
        this.emitNet();
        this.emitBreakdown();
    }

    /** Set every currently rendered series on one flow side to visible or hidden. */
    setSideSeriesVisibility(dir: 'in' | 'out', visible: boolean) {
        const seriesNames = (this.presentation?.currentSeries ?? [])
            .filter((series) => series.dir === dir)
            .map((series) => series.name);
        if (!seriesNames.length) {
            return;
        }
        this.isApplyingLegendSelection = true;
        for (const name of seriesNames) {
            if (visible) {
                this.hiddenSeries.delete(name);
            } else {
                this.hiddenSeries.add(name);
            }
            this.dashboardFlowChartAction({
                type: visible ? 'legendSelect' : 'legendUnSelect',
                name,
            });
        }
        this.isApplyingLegendSelection = false;
        this.emitNet();
        this.emitBreakdown();
    }

    private onLoaderResult(result: IDataLoaderResult) {
        if (
            result.isLoading ||
            result.status !== 'ok' ||
            !isFlowDashboardData(result.data)
        ) {
            this.forwardResult(result);
            return;
        }
        this.lastContext = result.queryContext;
        const data = result.data;
        this.rangeAggregation = data.rangeAggregation;
        this.aggregation = aggregateFlowRows(data.rows, {
            dateKey: this.dateKey,
            inflowKey: this.inflowKey,
            outflowKey: this.outflowKey,
            splitKey: this.splitKey,
            bucketKey: this.bucketKey,
            xAxisSortFn: this.xAxisSortFn,
        });
        this.hiddenSeries.clear();
        this.zoomStart = 0;
        this.zoomEnd = 100;
        this.emitFlow();
        this.refreshVisibleAggregates();
    }

    private forwardResult(result: IDataLoaderResult) {
        this.flowViewModel$.emit(result);
        this.netViewModel$.emit(result);
        this.inflowRows$.emit(result);
        this.outflowRows$.emit(result);
    }

    private emitFlow() {
        const { presentation, flow } = buildFlowPresentation({
            aggregation: this.aggregation,
            threshold: this.threshold,
            hiddenSeries: this.hiddenSeries,
            colorMode: this.colorMode,
            colorLayer: this.colorLayer,
            colorService: this.colorService,
            zoomStart: this.zoomStart,
            zoomEnd: this.zoomEnd,
        });
        this.presentation = presentation;
        this.flowViewModel$.emit(
            this.ok({
                ...flow,
                wheelZoom: this.wheelZoom,
                xAxisKey: this.dateKey,
            })
        );
        this.emitNet();
    }

    private emitNet() {
        const net = flowNetByDate(
            this.aggregation.dates,
            this.presentation?.currentSeries ?? [],
            this.hiddenSeries
        );
        const viewModel: NetChartViewModel = {
            dates: this.aggregation.dates,
            data: this.aggregation.dates.map((date) => +(net[date] || 0)),
            xAxisKey: this.dateKey,
            zoomStart: this.zoomStart,
            zoomEnd: this.zoomEnd,
            wheelZoom: this.wheelZoom,
        };
        this.netViewModel$.emit(this.ok(viewModel));
    }

    private onDataZoom(params: any) {
        const batch = params?.batch?.[0] ?? params;
        if (typeof batch?.start === 'number') {
            this.zoomStart = batch.start;
        }
        if (typeof batch?.end === 'number') {
            this.zoomEnd = batch.end;
        }
        this.refreshVisibleAggregates();
    }

    private onLegendSelectChanged(event: {
        selected: Record<string, boolean>;
    }) {
        if (this.isApplyingLegendSelection) {
            return;
        }
        this.hiddenSeries.clear();
        for (const [name, selected] of Object.entries(event.selected ?? {})) {
            if (!selected) {
                this.hiddenSeries.add(name);
            }
        }
        this.emitNet();
        this.emitBreakdown();
    }

    private refreshVisibleAggregates() {
        const dates = getFlowVisibleDates(
            this.aggregation.dates,
            this.zoomStart,
            this.zoomEnd
        );
        this.emitBreakdown(new Set(dates));
    }

    private emitBreakdown(
        visibleDates = new Set(
            getFlowVisibleDates(
                this.aggregation.dates,
                this.zoomStart,
                this.zoomEnd
            )
        )
    ) {
        if (!this.presentation) {
            return;
        }
        const options = {
            aggregation: this.aggregation,
            presentation: this.presentation,
            visibleDates,
            hiddenSeries: this.hiddenSeries,
            threshold: this.threshold,
            rangeAggregation: this.rangeAggregation,
        };
        this.inflowRows$.emit(
            this.ok(
                buildFlowBreakdown('in', {
                    ...options,
                    sort: this.breakdownSort.in,
                })
            )
        );
        this.outflowRows$.emit(
            this.ok(
                buildFlowBreakdown('out', {
                    ...options,
                    sort: this.breakdownSort.out,
                })
            )
        );
    }

    /** Dispatching lives here because the service still owns the mutable legend selection state. */
    private dashboardFlowChartAction(action: { type: string; name: string }) {
        // The interaction helper deliberately owns only wiring/connect lifecycle. The flow chart is exposed
        // through the registered ECharts listener, so dispatch via the same instance held by the helper.
        this.chartInteractions.dispatchFlowAction(action);
    }

    /** Wrap a view-model as an 'ok' loader result for a stock item. */
    private ok(
        data: FlowChartViewModel | NetChartViewModel | FlowBreakdownRow[]
    ): IDataLoaderResult {
        return {
            data,
            queryContext: this.lastContext!,
            isLoading: false,
            status: 'ok',
            statusMessage: '',
            sqlAndJsProcessing: '',
        };
    }
}
